<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  shallowRef,
  watch,
} from "vue";

import { InsightRunner, type TreeNode } from "../sbi/InsightRunner.ts";
import { EMBED_API_KEY, IS_LUCENT_DASHBOARD_KEY } from "../sbi/embed-context.ts";
import { PRINT_MODE_KEY } from "../sbi/print-mode.ts";
import { provideEmbedWidth } from "../sbi/embed-width.ts";
import {
  exitDocumentFullscreen,
  FULLSCREEN_EVENTS,
  fullscreenElement,
  isFullscreenSupported,
  requestElementFullscreen,
} from "./fullscreen.ts";
import type { ChangeValue, NamespacedState } from "../sbi/protocol";
import LucentSpinner from "./LucentSpinner.vue";
import InsightNode from "./InsightNode.vue";
import EmbedHostCodeTab from "./EmbedHostCodeTab.vue";
import EmbedHostVersionsTab from "./EmbedHostVersionsTab.vue";
import EmbedHostQueriesTab, { type QueryLogEntry } from "./EmbedHostQueriesTab.vue";
import {
  FILTER_KINDS,
  groupTopLevelTree,
  type RootGroup,
} from "./filter-bar-grouping.ts";

const props = defineProps<{
  /** Base URL of insights-api. Used for both source fetch and queries. */
  apiUrl: string;
  /** Insight id to render. */
  insightId: string;
  /** Bearer token scoped to `insightId`. */
  token: string;
  /** Pre-seed host-set viewer state (e.g. saved filter selections). */
  initialState?: Record<string, unknown>;
  /**
   * Pre-seed the author-state half of the namespaced viewer state
   * (i.e. values normally written via `sbi.state(k, …)` inside the
   * insight). Honoured ONLY when the embedder page is same-origin
   * with `apiUrl`; cross-origin pages have this silently dropped so
   * a malicious page can't forge an "open drilldown" on first paint.
   *
   * The dashboard's PDF export flow uses this to round-trip the
   * live drilldown selection through the print template (the
   * template runs same-origin under headless Chromium so the gate
   * lets it through).
   */
  initialAuthorState?: Record<string, unknown>;
  /**
   * Print mode. When true the embed:
   *   - disables chart animation (charts pass `animation: false` to
   *     ECharts so the first paint is the final paint);
   *   - flips interactive controls (selects, date pickers, toggles,
   *     etc.) to static labelled text — a disabled-looking dropdown
   *     in a PDF reads as broken, a `Region: Metro` line reads as
   *     "this is the value the report was generated for";
   *   - hides the KPI info-button affordance (its description still
   *     reaches the printed footnote layer via a data attribute);
   *   - emits a single `lucent-print-ready` DOM event when first
   *     render lands AND no queries are outstanding, which is the
   *     signal the headless Playwright renderer waits on.
   *
   * Provided to descendants via the `PRINT_MODE_KEY` inject key so
   * chart and control components flip their render without a prop
   * chain through every InsightNode level.
   */
  print?: boolean;
  /**
   * Show the chrome toolbar (tab strip + fullscreen button) above
   * the insight body.
   *
   * **Default: false.** Embeds render chrome-free unless the embedder
   * explicitly opts in by setting `toolbar="true"` on the host element
   * — the dashboard and any embedder that wants the debug tabs +
   * fullscreen affordance must pass it. Marketing-hero / thumbnail /
   * host-page-driven embeds get a clean canvas with no opt-out work.
   *
   * Accepts truthy forms: the boolean `true`, or the strings `"true"`,
   * `"1"`, `"yes"`, `"on"`, or empty string (HTML attribute-presence
   * semantics). Anything else — including the literal string
   * `"false"`, `"0"`, or the attribute being absent — keeps the
   * toolbar hidden.
   *
   * **Fullscreen is gated on this flag.** The fullscreen button is the
   * only embed-side fullscreen trigger; hiding the toolbar removes it.
   * The `:host(:fullscreen)` boardroom-projection styling (paper
   * background, 24px bezel padding, `--lucent-font-scale: 1.4`) is
   * also gated on the `[toolbar]` attribute being present, so even if
   * a host page calls `element.requestFullscreen()` directly on a
   * toolbar-less embed, the curated boardroom presentation does not
   * engage. The user can still Escape out of the browser-native
   * fullscreen state.
   *
   * Print mode is unaffected by this flag (print suppresses chrome
   * via the `.lucent-insight-host--print` selector regardless).
   */
  toolbar?: boolean | string;
}>();

/**
 * Normalised toolbar flag. HTML custom-element attributes are always
 * strings (Vue can't coerce when the prop type is `boolean | string`
 * without runtime metadata), so accept the truthy form variants
 * explicitly. Absence of the attribute / explicit "false" / "0" all
 * resolve to `false` so chrome-free is the durable default.
 */
const toolbarEnabled = computed<boolean>(() => {
  const v = props.toolbar;
  if (typeof v === "boolean") return v;
  if (typeof v !== "string") return false;
  const lowered = v.trim().toLowerCase();
  return (
    lowered === "" ||
    lowered === "true" ||
    lowered === "1" ||
    lowered === "yes" ||
    lowered === "on"
  );
});

// Make print mode visible to every descendant render-op component.
// `provide` runs in setup synchronously, so the value is in scope
// before any child mounts — no flash of interactive-control then
// switch-to-static.
provide(PRINT_MODE_KEY, () => props.print === true);

/**
 * Chrome opt-ins for the Queries / Versions debug tabs. Server-side
 * decisions persisted on the embed-token row and echoed back to the
 * runtime on the source-fetch response (see `/api/embed/insights/:id/
 * source` in insights-api). The consumer page can't override — a
 * customer dropping `<lucent-insight>` into their product doesn't get
 * to flip on the SQL log unless the minting backend opted in for them.
 *
 * Defaults stay false so a malformed / pre-migration response (no
 * fields on the body) keeps production embeds chrome-free. Both refs
 * are reset on every `startRunner` so token swaps pick up the new
 * mint's flags cleanly.
 */
const showQueries = ref(false);
const showVersions = ref(false);
const showCode = ref(false);

/**
 * Lucent-dashboard vs external-embed signal. Server-derived from the
 * verified embed-token's `sub` claim (see `is_lucent_dashboard` on
 * the source-fetch response). Drives host-side rendering decisions
 * that only apply when the insight is being viewed in the Lucent
 * dashboard authoring/preview surface — currently the `hideOnEmbed`
 * filter opt, potentially other dashboard-only affordances later.
 *
 * Defaults to false so a pre-migration server response (missing the
 * field) is treated as an external embed and dashboard-only
 * affordances stay hidden — the safer fallback for customer pages.
 * Provided to descendants via `IS_LUCENT_DASHBOARD_KEY` so deeply
 * nested filter components can read it without a prop chain.
 */
const isLucentDashboard = ref(false);
provide(IS_LUCENT_DASHBOARD_KEY, () => isLucentDashboard.value);
// For components that fetch on their own (the document "View" panel).
// A thunk, so a token swap is picked up at the next call.
provide(EMBED_API_KEY, () =>
  props.apiUrl && props.token && props.insightId
    ? { apiUrl: props.apiUrl, token: props.token, insightId: props.insightId }
    : null,
);

/**
 * Filter keys pinned by the embed token. Sourced from the server-
 * authoritative `locked_filters` field on the source-fetch response;
 * stays empty when the token doesn't pin any. Used to:
 *
 *   - seed the runner's `locked` namespace so the worker's merged
 *     state view returns the pinned value (worker spread order:
 *     `{...host, ...author, ...locked}` — locked wins);
 *   - disable / strip matching filter controls from the rendered
 *     tree so the viewer can't even attempt to change them.
 *
 * The consumer page cannot override this — it reflects what the
 * token was minted with, decided server-side at mint time.
 */
const lockedFilters = ref<Record<string, unknown>>({});
const lockedFilterKeys = computed<ReadonlySet<string>>(
  () => new Set(Object.keys(lockedFilters.value)),
);

/**
 * Raw insight source — the pre-SQL-rewrite JS body the author wrote.
 * Only populated when the embed token opted into `show_code`; the
 * source-fetch response omits the field otherwise so chrome-free
 * embeds don't pull the raw source over the wire.
 */
const sourceCode = ref<string>("");

/**
 * Insight metadata echoed from the source-fetch payload. Used to
 * fill out the Code tab's stats table alongside the regex-derived
 * counts. Both stay empty for tokens that didn't opt into
 * `show_code` so the table simply doesn't render those rows.
 */
const sourceApiVersion = ref<string>("");
const sourceVersion = ref<number | null>(null);

const emit = defineEmits<{
  /** Fires once the worker has produced its first render. */
  (e: "ready"): void;
  /** Fires on any fatal runtime error. Detail is the message. */
  (
    e: "error",
    payload: {
      code: string;
      message: string;
      insightId?: string;
      suggestedReplacement?: string;
      originalCall?: string;
      instancePath?: string;
    },
  ): void;
  /**
   * Fires for every `sbi.branch.if` / `sbi.branch.when` container after
   * the test marker resolves. Recoverable: `errored: true` indicates the
   * test query rejected — the conditional section renders a "couldn't
   * load" banner; sibling sections render normally. The wrapper Web
   * Component translates `errored: true` into a DOM `lucent-error` event
   * with `code: "branch_test_failed"` so embedders can react (retry,
   * hide, log) without blowing away the rest of the page.
   */
  (
    e: "branch-resolved",
    payload: {
      arm: "then" | "else";
      whenLabel: string;
      errored?: boolean;
      errorMessage?: string;
    },
  ): void;
  /**
   * Forwarded chart click. Embedders consume via `lucent-click` DOM
   * event; the typed `datum` shape varies per chart type (see briefing
   * click-payload reference table).
   */
  (
    e: "click",
    payload: { targetId: string; datum?: Record<string, unknown>; insightId: string },
  ): void;
  /**
   * Forwarded non-fatal diagnostic from a per-chart component (sankey
   * cycle detection, sunburst depth-cap truncation, etc.). Coalesced
   * by code per render pass — repeated emissions of the same code
   * within a pass surface as a single event with `count: N`. Round-3
   * Integrator dedupe contract.
   */
  (
    e: "diagnostic",
    payload: {
      code: string;
      insightId: string;
      count: number;
      detail?: Record<string, unknown>;
    },
  ): void;
  /**
   * Fires on every `sbi.ui.accordion(...)` `<details>` toggle. The
   * wrapper Web Component translates this to a DOM `accordion-toggle`
   * CustomEvent — integrators consume for telemetry ("which sections
   * do users open?").
   */
  (
    e: "accordion-toggle",
    payload: { summary: string; open: boolean; insightId: string },
  ): void;
  /**
   * Fires once per `sbi.ui.callout(...)` mount in the render tree.
   * Translated to a DOM `callout-rendered` CustomEvent on the wrapper.
   * Useful for badging a host-page sidebar with danger-callout counts
   * or driving brand-side alert chrome.
   */
  (
    e: "callout-rendered",
    payload: { kind: string; title?: string; insightId: string },
  ): void;
  /**
   * Fires once per **committed** viewer-state mutation (either a
   * `sbi.state(...)` setter inside the worker or the host calling
   * `setState(...)` on the element). Coalesced or cancelled
   * mutations do NOT emit, so `liveState` mirrors what the embed
   * actually rendered.
   *
   * Payload is the namespaced `{host, author}` envelope. Consumers
   * reading flat keys should now read `detail.host[k]` or
   * `detail.author[k]` explicitly (DOM contract v2; the previous
   * flat-merged shape is removed).
   */
  (e: "state-change", payload: NamespacedState): void;
  /**
   * Fires when a new render pass starts — either the host or
   * insight code mutated state and the commit window has flushed.
   * Pairs with `rerun-end` so consumers can flip `aria-busy`
   * symmetrically. Not emitted before the first render lands.
   */
  (e: "rerun-start"): void;
  /**
   * Fires when the current pass terminates. `status` is `"committed"`
   * when every patcher landed cleanly, `"errored"` when author code
   * threw mid-pass. Cancelled passes do NOT emit `rerun-end` on the
   * cancel — the superseding pass fires its own.
   */
  (e: "rerun-end", payload: { status: "committed" | "errored" }): void;
  /**
   * Print-mode readiness signal. Fired ONCE, only when `print=true`,
   * once both:
   *
   *   - the worker has produced its first `render` (so the structural
   *     tree is on screen), AND
   *   - every dispatched query has resolved (the pending counter is
   *     zero).
   *
   * The print-template bootstrap awaits this event before flipping
   * `window.__lucentReady = true`. With `animation: false` propagated
   * to every chart via the same `print` flag, the first paint is the
   * final paint — no wall-clock grace period required.
   *
   * Translated to a DOM `lucent-print-ready` event on the wrapper.
   * Not fired outside print mode; non-print embeds use `ready`.
   */
  (e: "print-ready"): void;
}>();


// shallowRef avoids deep-reactivity recursion on the (potentially
// large) tree of render ops.
const tree = shallowRef<TreeNode[]>([]);
// Per-pass field → format string registry produced from any
// `sbi.format(...)` calls in the worker. Held alongside the tree so
// downstream nodes can resolve a default format for values drawn
// from a known field. The map is replaced wholesale on every render
// — registrations don't accumulate across passes.
const formats = shallowRef<Record<string, string>>({});

// Per-runner log of every dispatched query. Cleared whenever the runner
// is rebuilt (prop change). The whole array is replaced on each update
// to keep `shallowRef` semantics — Vue would otherwise miss mutations
// to nested entries.
const queries = shallowRef<QueryLogEntry[]>([]);

/**
 * Currently-visible chrome tab. `queries`, `versions`, and `code` only
 * render when their respective server flag is on. The "Insight" tab
 * button itself only renders when at least one of those debug tabs is
 * also on — a lone non-clickable "Insight" pill is just noise, so when
 * there are no siblings the tab strip collapses and the insight body
 * renders bare under the (always-present) fullscreen toolbar.
 */
type ChromeTab = "insight" | "queries" | "versions" | "code";
const activeTab = ref<ChromeTab>("insight");
const hasExtraTabs = computed(
  () => showQueries.value || showVersions.value || showCode.value,
);

/**
 * Two-phase load model. The structural shell that the worker emits
 * as its first `render` already encodes per-component loading state
 * (any leaf bound to `sbi.lazy` arrives with `loading: true`), so we
 * don't need a generic card-skeleton intermediate phase anymore — the
 * insight's actual layout IS the loading state.
 *
 * - "booting" — initial state on mount. Covers source fetch,
 * esbuild-wasm bundling, Web Worker boot, runtime
 * handshake. We have no layout to show yet, so a
 * centered spinner is the honest visual.
 * - "ready"   — first `render` has landed. From this point on the
 * real tree is on screen; whether individual leaves
 * are still loading is encoded per-node by the
 * worker.
 *
 * `rerunning` is orthogonal to `phase`: it flips on/off whenever a
 * `state_set` triggers a re-execution AFTER the first render. It's
 * a fallback "everything's refreshing" signal that components use
 * when they don't carry their own per-op `loading` flag.
 */
type Phase = "booting" | "ready";
const phase = ref<Phase>("booting");
const rerunning = ref(false);
const errorMessage = ref<string | null>(null);

/**
 * Template ref on the outer wrapper. Used by `getHostElement()` to
 * walk up to the `<lucent-insight>` custom element through the
 * ShadowRoot — that's the element we ask the Fullscreen API to
 * promote so the host page's CSS can't bleed in, and so the user's
 * `<lucent-insight style="--lucent-font-scale: …">` override still
 * cascades into the embed.
 */
const rootEl = ref<HTMLElement | null>(null);

// One ResizeObserver on the root feeds every descendant's
// `useEmbedWidth()`. Declared here rather than beside the
// PRINT_MODE_KEY provide because it needs `rootEl`; `provide` only
// has to run during setup, and children don't mount until it ends.
const embedWidth = provideEmbedWidth(rootEl, () => props.print === true);
// The host is the `lucent-embed` container, and a container query can't
// restyle the container itself — so its own `gap` narrows from script.
// Print buckets as "wide", matching the container rules never firing there.
const hostNarrow = computed(() => embedWidth.bucket.value === "narrow");

/**
 * Tracks `document.fullscreenElement === host`. Drives the toolbar
 * button's icon swap, `aria-label`, `aria-pressed`, and `title`.
 * Updated by `fullscreenchange` so the state stays in sync when the
 * user hits Escape (which bypasses our click handler).
 */
const isFullscreen = ref(false);

/**
 * Element fullscreen is unavailable on iPhone Safari (and in iframes
 * without `allowfullscreen`, which `fullscreenEnabled` also reports).
 * Hide the button there rather than offer a control that only ever
 * emits `fullscreen_unavailable`.
 */
const fullscreenSupported =
  typeof document !== "undefined" && isFullscreenSupported(document);

/**
 * The `<lucent-insight>` custom element hosting this EmbedHost. We
 * mount inside a shadow root — `getRootNode()` returns that root,
 * and `.host` is the custom element above it. That's the element
 * we want to fullscreen (not the inner wrapper) so the
 * `:host(:fullscreen)` selector matches and the host page's CSS
 * stays out.
 *
 * Falls back to the wrapper itself for non-shadow mounts (tests,
 * direct Vue mounts in dev pages).
 */
const getHostElement = (): HTMLElement | null => {
  const wrapper = rootEl.value;
  if (!wrapper) return null;
  const root = wrapper.getRootNode();
  if (typeof ShadowRoot !== "undefined" && root instanceof ShadowRoot) {
    return root.host as HTMLElement;
  }
  return wrapper;
};

/**
 * Toolbar fullscreen button click. Soft-errors via `lucent-error`
 * with `code: "fullscreen_unavailable"` if the API rejects — most
 * commonly an iframe parent that didn't set `allowfullscreen`. The
 * embed keeps rendering normally; the click just couldn't promote.
 */
const toggleFullscreen = async (): Promise<void> => {
  const host = getHostElement();
  if (!host) return;
  try {
    if (fullscreenElement(document)) {
      await exitDocumentFullscreen(document);
    } else {
      await requestElementFullscreen(host);
    }
  } catch (err) {
    emit("error", {
      code: "fullscreen_unavailable",
      message: err instanceof Error ? err.message : "fullscreen request failed",
      insightId: props.insightId,
    });
  }
};

/**
 * `fullscreenchange` listener — fires on Escape exits too, which is
 * why we don't manage `isFullscreen` purely from the click handler.
 * We compare against the host element so a fullscreen elsewhere on
 * the page (a video, another widget) doesn't flip our icon.
 */
const onFullscreenChange = (): void => {
  const host = getHostElement();
  isFullscreen.value = host !== null && fullscreenElement(document) === host;
};

// Per-render-pass diagnostic dedupe. Round-3 Integrator dedupe
// contract: each chart emits ≤ 1 `lucent-diagnostic` per code per
// render pass. Repeated emissions of the same code coalesce into one
// outbound event with `count: N` in `detail`. The Map is replaced
// wholesale on each new render (cleared in `onRender`).
let diagnosticBuffer = new Map<string, { count: number; detail?: Record<string, unknown> }>();
let diagnosticFlushScheduled = false;
// Per-render-pass branch-resolved dedupe. With per-leaf streaming
// `onPatch` may be called many times per pass, but `branch-resolved`
// should fire at most once per branch frame per pass. Key = stable
// label of the if-frame. Cleared on each `onRender` (pass boundary).
let emittedBranchKeys = new Set<string>();

const flushDiagnostics = (): void => {
  if (diagnosticBuffer.size === 0) {
    diagnosticFlushScheduled = false;
    return;
  }
  for (const [code, entry] of diagnosticBuffer) {
    emit("diagnostic", {
      code,
      insightId: props.insightId,
      count: entry.count,
      detail: entry.detail,
    });
  }
  diagnosticBuffer = new Map();
  diagnosticFlushScheduled = false;
};

// Saved-view apply-time diagnostics — surfaced in the debug tab when
// `show_queries` is on (same surface as host/author state-collision
// warnings). When the debug tab is off, fall back to `console.warn`
// so dev-mode catches the same signal without requiring a token
// remint with `show_queries=true`. The list is intentionally narrow
// — only codes that signal "a saved view referenced something the
// current source no longer has" warrant a console fallback; chart-
// level diagnostics (sankey cycles, etc.) stay in the dedupe pipeline.
const CONSOLE_FALLBACK_CODES: ReadonlySet<string> = new Set([
  "state_key_ignored",
  "filter_value_invalid",
]);

const onDiagnostic = (code: string, detail: Record<string, unknown>): void => {
  const existing = diagnosticBuffer.get(code);
  if (existing) {
    existing.count += 1;
  } else {
    diagnosticBuffer.set(code, { count: 1, detail });
  }
  if (!showQueries.value && CONSOLE_FALLBACK_CODES.has(code)) {
    console.warn(`[lucent-insight] ${code}`, detail);
  }
  // Microtask flush — coalesces every same-code emission within the
  // current render pass into one outbound event. Embedders see the
  // aggregated `count` rather than a per-occurrence firehose.
  if (!diagnosticFlushScheduled) {
    diagnosticFlushScheduled = true;
    queueMicrotask(flushDiagnostics);
  }
};

let runner: InsightRunner | null = null;

/**
 * Author-state-seed gate. A cross-origin embed page could otherwise
 * set `el.initialAuthorState = {selected: "x"}` to forge an open
 * drilldown on first paint, so the prop is only honoured when the
 * embedder page is trusted:
 *
 *   1. Same origin as `apiUrl` — the PDF print template runs under
 *      headless Chromium against insights-api directly, so the
 *      strict same-origin check lets it through.
 *   2. Origin appears in `VITE_TRUSTED_EMBEDDER_ORIGINS` — explicit
 *      allowlist of first-party embedders baked into the bundle at
 *      build time. The dashboard-app's origin (a different subdomain
 *      of the same parent domain as insights-api) goes here so saved
 *      views can re-seed `author` state (filter selections, drilldown
 *      gating values) on load. Comma-separated, exact-match per
 *      origin — no wildcards, no parent-domain suffix matching.
 *
 * Threat model unchanged: arbitrary cross-origin customer portals
 * still can't pre-seed author state.
 */
const TRUSTED_EMBEDDER_ORIGINS: ReadonlySet<string> = new Set(
  (import.meta.env.VITE_TRUSTED_EMBEDDER_ORIGINS ?? "")
    .split(",")
    .map((s: string) => s.trim())
    .filter((s: string) => s.length > 0),
);

const isAuthorSeedAllowed = (apiUrl: string): boolean => {
  try {
    if (new URL(apiUrl).origin === window.location.origin) return true;
  } catch {
    return false;
  }
  return TRUSTED_EMBEDDER_ORIGINS.has(window.location.origin);
};

/**
 * Coerce an `initial-state` / `initial-author-state` prop to a plain
 * object. HTML embedders set these via the kebab-case attribute (e.g.
 * `<lucent-insight initial-state='{"clubMemberId":"1"}'>`), and Vue's
 * `defineCustomElement` hands them through as raw strings because the
 * props are declared via type-only `defineProps<{…}>()` — there's no
 * runtime Object constructor for the custom-element layer to drive
 * auto-JSON-parse off. Without this coercion, `{ ...stringValue }` in
 * `HostStateMachine.seed` spreads character-by-character and the first
 * `state-change` event emits `{0:"{",1:"\"",2:"c",…}` instead of the
 * decoded object.
 *
 * Strings that don't parse to a plain object are silently dropped — a
 * malformed `initial-state` is treated the same as the attribute being
 * absent rather than crashing the embed.
 */
const coerceStateProp = (
  value: unknown,
): Record<string, unknown> | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // fall through to undefined
    }
  }
  return undefined;
};

/**
 * Print-mode readiness bookkeeping. Only meaningful when
 * `props.print === true`. `print-ready` fires once all of the
 * following hold:
 *
 *   - the worker has produced at least one `render` (so we have a
 *     tree on screen);
 *   - the pending-query counter is zero (no `/api/query/batch`
 *     in flight);
 *   - the latest tree carries no leaf with `loading: true` — i.e.
 *     every KPI / chart / table / conditional has actual data, not
 *     a skeleton placeholder. This is the gate that survives the
 *     shell-first paint, where `onRender` lands BEFORE queries are
 *     dispatched and `printPendingQueries` is still 0.
 *
 * The latch (`printReadyFired`) guards against firing twice on a
 * re-render triggered by initial state. Re-armed only on a runner
 * rebuild (`startRunner`).
 */
let printPendingQueries = 0;
let printReadyFired = false;
let printRenderHasLanded = false;

/**
 * True when any node in the tree is still in `loading: true`. The
 * shell render emits the structural skeleton with this flag set on
 * every leaf bound to a query marker; patches flip it off as each
 * query resolves. JSON-stringify is overkill structurally but
 * keeps this dependency-free and won't go stale when new node kinds
 * are added — the only false positive is a string field literally
 * containing `"loading":true`, which would be a freak collision in
 * SBI source.
 */
const treeHasLoadingLeaves = (nodes: readonly TreeNode[]): boolean => {
  try {
    return JSON.stringify(nodes).includes('"loading":true');
  } catch {
    return false;
  }
};

const maybeFirePrintReady = (): void => {
  if (props.print !== true) return;
  if (printReadyFired) return;
  if (!printRenderHasLanded) return;
  if (printPendingQueries > 0) return;
  if (treeHasLoadingLeaves(tree.value)) return;
  printReadyFired = true;
  emit("print-ready");
};

/**
 * Source-fetch response. `content` is the JS body the runtime then
 * esbuild-bundles for the Worker; the two boolean flags come from
 * the verified embed-token's row (see `EmbedTokenClaims.showQueries`
 * / `showVersions`). Both default to false on absent or malformed
 * shapes so pre-migration servers + old clients keep working
 * unchanged.
 */
interface SourceResponse {
  content?: string;
  show_queries?: boolean;
  show_versions?: boolean;
  show_code?: boolean;
  /**
   * True when the token was minted via a user JWT (Lucent dashboard
   * authoring/preview); false when minted via a client service key
   * (external embed). Server-authoritative; the consumer page can't
   * override. Drives the `hideOnEmbed` filter opt — see
   * `embed-context.ts`.
   */
  is_lucent_dashboard?: boolean;
  /**
   * Raw (pre-SQL-rewrite) JS body. Only present when the embed
   * token opted into `show_code`; absent otherwise.
   */
  source_code?: string;
  /**
   * SBI API version the insight was authored against. Surfaced on
   * the Code tab's stats table; not used to gate behaviour.
   */
  api_version?: string;
  /** Insight version number persisted at save time. */
  version?: number;
  /**
   * Filter keys pinned by the embed token, with their pinned values.
   * Empty `{}` when the token didn't pin any. Server-authoritative;
   * the runtime seeds the `locked` namespace from this map.
   */
  locked_filters?: Record<string, unknown>;
}

/**
 * Fetch the JS source for `insightId` from the API.
 *
 * Returns null on auth failures, mapping the `code` field from the API
 * to the `error` event so the consumer can re-mint a token if it sees
 * `token_expired`. On success returns the parsed payload — the caller
 * pulls `content` for the Worker and stashes the chrome flags into
 * the local refs that drive tab rendering.
 */
const fetchSource = async (): Promise<SourceResponse | null> => {
  const url = `${props.apiUrl.replace(/\/$/, "")}/api/embed/insights/${props.insightId}/source`;
  const response = await fetch(url, {
    headers: { authorization: `Bearer ${props.token}` },
  });
  if (!response.ok) {
    let code = "fetch_failed";
    let message = `failed to fetch source: HTTP ${response.status}`;
    try {
      const body = (await response.json()) as { code?: string; message?: string };
      if (body.code) code = body.code;
      if (body.message) message = body.message;
    } catch {
      /* not JSON */
    }
    errorMessage.value = message;
    emit("error", { code, message });
    return null;
  }
  const payload = (await response.json()) as SourceResponse;
  if (!payload.content) {
    const message = "source response missing content";
    errorMessage.value = message;
    emit("error", { code: "malformed_response", message });
    return null;
  }
  return payload;
};

const startRunner = async (): Promise<void> => {
  phase.value = "booting";
  rerunning.value = false;
  errorMessage.value = null;
  // Clear any prior log cache when the runner is rebuilt —
  // id/token swaps and forced remounts shouldn't leak state from the
  // previous insight. Chrome flags are also reset so a swap to a
  // token without `show_queries` flips the tab off immediately.
  // The Versions sub-component manages its own state reset internally.
  queries.value = [];
  showQueries.value = false;
  showVersions.value = false;
  showCode.value = false;
  isLucentDashboard.value = false;
  lockedFilters.value = {};
  sourceCode.value = "";
  sourceApiVersion.value = "";
  sourceVersion.value = null;
  activeTab.value = "insight";
  // Reset print-mode bookkeeping for a fresh runner — pending-query
  // counter and the "have we fired print-ready yet?" latch both
  // start from zero so a token swap doesn't replay a stale signal.
  printPendingQueries = 0;
  printReadyFired = false;
  printRenderHasLanded = false;

  const payload = await fetchSource();
  if (payload === null || !payload.content) return;
  showQueries.value = payload.show_queries === true;
  showVersions.value = payload.show_versions === true;
  showCode.value = payload.show_code === true;
  isLucentDashboard.value = payload.is_lucent_dashboard === true;
  // Server-authoritative locked-filter envelope. Guarded against
  // non-object payloads so a malformed server response doesn't break
  // the runner — falls back to `{}` (no filters pinned).
  lockedFilters.value =
    payload.locked_filters !== null &&
    typeof payload.locked_filters === "object" &&
    !Array.isArray(payload.locked_filters)
      ? { ...payload.locked_filters }
      : {};
  if (showCode.value && typeof payload.source_code === "string") {
    sourceCode.value = payload.source_code;
    if (typeof payload.api_version === "string") {
      sourceApiVersion.value = payload.api_version;
    }
    if (typeof payload.version === "number") {
      sourceVersion.value = payload.version;
    }
  }

  // Author-state-seed gate — see `isAuthorSeedAllowed` above for
  // rationale. Third-party embed pages silently drop the prop;
  // same-origin (print template) and allowlisted first-party
  // embedders (dashboard-app, for saved-view restore) let it through.
  const allowAuthorSeed = isAuthorSeedAllowed(props.apiUrl);
  const seededHost = coerceStateProp(props.initialState);
  const seededAuthor = coerceStateProp(props.initialAuthorState);
  runner = new InsightRunner({
    source: payload.content,
    apiUrl: props.apiUrl,
    insightId: props.insightId,
    token: props.token,
    ...(seededHost !== undefined ? { initialState: seededHost } : {}),
    ...(allowAuthorSeed && seededAuthor !== undefined
      ? { initialAuthorState: seededAuthor }
      : {}),
    // Seed the `locked` namespace before the first pass. The runner
    // hands this to `HostStateMachine.seed`, which makes the very
    // first `ready_ack` carry the pinned values in `state.locked`.
    ...(Object.keys(lockedFilters.value).length > 0
      ? { initialLockedState: { ...lockedFilters.value } }
      : {}),
    // Mirror every COMMITTED state mutation out as a DOM event so the
    // embedding page can capture filter / drilldown selections at
    // click time (e.g. for the PDF export route). Coalesced and
    // cancelled mutations don't emit — the payload always matches
    // what the embed actually rendered.
    onStateChange: (state) =>
      emit("state-change", {
        host: { ...state.host },
        author: { ...state.author },
        // Locked is server-authoritative and won't change for the
        // life of the runner, but emit it on every commit so
        // consumers reading the DOM event get a self-contained
        // namespaced snapshot instead of having to remember the
        // initial source-fetch payload.
        locked: { ...state.locked },
      }),
    onDiagnostic: (code, detail) => onDiagnostic(code, detail),
    // Track every dispatched query and its resolution. The callbacks
    // fire whether or not the Queries tab is visible — the cost is a
    // small append per query — so flipping `show-queries` on/off
    // dynamically doesn't need a runner restart. The host runner
    // already exposes both hooks as optional; passing them
    // unconditionally is simpler than gating on `showQueries.value`.
    onQuery: (q) => {
      queries.value = [
        ...queries.value,
        {
          id: q.id,
          sql: q.sql,
          params: q.params,
          startedAt: performance.now(),
          status: "pending",
        },
      ];
      printPendingQueries += 1;
    },
    onQueryResolved: (r) => {
      printPendingQueries = Math.max(0, printPendingQueries - 1);
      maybeFirePrintReady();
      // `entry.sql` was the registry UUID at dispatch time — source-serve
      // rewrote the raw SQL out of the worker bundle, so the UUID is all
      // the host sees until the server echoes the resolved query text in
      // the response. Swap it in here when present; fall back to the
      // UUID for pre-resolution failures (`unknown_query_id`,
      // `missing_query`) where the server has no SQL to send.
      const now = performance.now();
      queries.value = queries.value.map((entry) => {
        if (entry.id !== r.id) return entry;
        const durationMs = Math.round(now - entry.startedAt);
        const sql = r.sql ?? entry.sql;
        if (r.status === "ok") {
          // `cached` is photon's verdict, forwarded to the host only. Left
          // undefined against a server that doesn't send it, which the tab
          // renders as "no badge" rather than as a miss.
          return {
            ...entry,
            sql,
            status: "ok",
            rows: r.rows,
            durationMs,
            ...(r.cached !== undefined ? { cached: r.cached } : {}),
          };
        }
        return {
          ...entry,
          sql,
          status: "error",
          durationMs,
          errorMessage: r.message,
          errorCode: r.code,
        };
      });
    },
    // We don't act on `onWorkerStarted` anymore — the next interesting
    // visual is the worker's first `render`, which is already the
    // structural shell with per-leaf loading flags. Holding on the
    // booting spinner until that lands gives a cleaner crossfade than
    // showing a card-skeleton silhouette in between.
    onWorkerStarted: () => {},
    onRender: (next, nextFormats) => {
      // Pass boundary. Flush + reset per-pass dedupe state so the
      // next pass's diagnostics/branch-resolutions don't pile onto
      // the previous one. `onPatch` does NOT reset these — it's
      // incremental.
      flushDiagnostics();
      emittedBranchKeys = new Set<string>();
      tree.value = next;
      formats.value = nextFormats;
      // NOTE: `rerunning.value = false` lives in `onRerunEnd` now —
      // the shell render is the start of the visible pass, but the
      // pass isn't committed until every patcher has settled (or
      // the pass errors). Flipping `rerunning` here would clear the
      // overlay before the data lands.
      if (phase.value !== "ready") {
        phase.value = "ready";
        emit("ready");
      }
      // Mark first render as landed; combined with the pending-query
      // counter, this gates `print-ready`. Repeated `onRender` calls
      // are no-ops thanks to the latch inside `maybeFirePrintReady`.
      printRenderHasLanded = true;
      maybeFirePrintReady();
      // Walk for branches that resolved synchronously (rare on
      // first paint — most are still `loading: true` on the shell;
      // they light up via `onPatch` as their tests resolve).
      emitBranchResolutions(next);
    },
    /**
     * Incremental update from the worker — one of the queries
     * backing this pass finished resolving and its op has been
     * patched into the tree. Replace `tree.value` so Vue's keyed
     * v-for re-renders just the affected nodes; walk for any
     * newly-resolved branches and emit `branch-resolved` (deduped
     * via `emittedBranchKeys`). DON'T flush diagnostics or reset
     * any pass-scoped state — patches are NOT pass boundaries.
     */
    onPatch: (next) => {
      tree.value = next;
      emitBranchResolutions(next);
      // A patch is what flips a leaf from `loading: true` to its
      // resolved shape. Re-check readiness so the final query's
      // patch lands ready as soon as the tree is fully populated —
      // we can't rely on `onQueryResolved` alone because the
      // pending-query counter goes to zero one tick before the
      // worker emits its corresponding `render_patch`.
      maybeFirePrintReady();
    },
    // Fires whenever a new pass starts — previous tree is stale,
    // waiting on a new render. Only meaningful AFTER the first
    // render; before then `phase` covers the visual state on its
    // own. Pairs with `onRerunEnd` so `aria-busy` flips symmetrically.
    onRerunStart: () => {
      if (phase.value === "ready") {
        rerunning.value = true;
        emit("rerun-start");
      }
    },
    onRerunEnd: (status) => {
      rerunning.value = false;
      if (phase.value === "ready") emit("rerun-end", { status });
    },
    onError: (err) => {
      errorMessage.value = err.message;
      // NOTE: don't clear `rerunning` here — `onRerunEnd` fires
      // alongside `onError` (the state machine notes the error
      // before surfacing it) with `status: "errored"`, which
      // handles the clear.
      // Map render-op validator failures to the `chart_spec_invalid`
      // taxonomy code so embedders can route those separately from
      // generic runtime exceptions. The InvalidRenderOps message
      // includes the Ajv `instancePath` (`op[N] failed schema: ...
      // at /spec/...`) — strip and forward as `instancePath` for the
      // typed payload.
      const isRenderOpFailure =
        err.name === "InvalidRenderOps" || /failed schema:/.test(err.message);
      if (isRenderOpFailure) {
        const instancePath = err.message.match(/ at (\/[^\s]*)/)?.[1];
        emit("error", {
          code: "chart_spec_invalid",
          message: err.message,
          insightId: props.insightId,
          ...(instancePath ? { instancePath } : {}),
        });
      } else {
        emit("error", {
          code: "runtime_error",
          message: err.message,
          insightId: props.insightId,
        });
      }
    },
  });
  await runner.start();
};

/**
 * Walk the tree once and emit `branch-resolved` for every `if`-frame
 * whose test has resolved (`loading !== true`) AND hasn't already
 * been emitted in this pass. Recurses through containers (columns,
 * tabs, nested if). The shell-paint render has `loading: true` on
 * every if-frame so this is a no-op there; the patch path lights
 * them up as their test queries resolve.
 *
 * Dedupe key combines the if-frame's `whenLabel` and a path-derived
 * suffix so two if-frames sharing a label at different positions
 * don't share dedupe state. Sibling labels at the same depth are
 * disambiguated by index in the parent.
 */
const emitBranchResolutions = (nodes: TreeNode[], pathPrefix = ""): void => {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!;
    const here = `${pathPrefix}/${i}`;
    if (n.kind === "if") {
      if (n.loading !== true) {
        const key = `${here}#${n.whenLabel}`;
        if (!emittedBranchKeys.has(key)) {
          emittedBranchKeys.add(key);
          const payload: {
            arm: "then" | "else";
            whenLabel: string;
            errored?: boolean;
            errorMessage?: string;
          } = { arm: n.arm, whenLabel: n.whenLabel };
          if (n.errored) payload.errored = true;
          if (n.errorMessage) payload.errorMessage = n.errorMessage;
          emit("branch-resolved", payload);
        }
      }
      // Recurse into the chosen arm's children — nested branches
      // resolve independently.
      emitBranchResolutions(n.children, here);
    } else if (n.kind === "columns") {
      for (let c = 0; c < n.cells.length; c++) {
        emitBranchResolutions(n.cells[c]!.children, `${here}/cell${c}`);
      }
    } else if (n.kind === "tabs") {
      for (const [name, arr] of Object.entries(n.tabs)) {
        emitBranchResolutions(arr, `${here}/tab:${name}`);
      }
    }
  }
};

/**
 * Forward click events from rendered ops back to the worker. The
 * runner only acts on click events whose target id was registered via
 * `subscribe_click` from the worker, so calling this on every click
 * is safe — unsubscribed targets are dropped silently.
 */
const onClickTarget = (
  id: string,
  datum?: Record<string, unknown>,
  rowIndex?: number,
  event?: Event,
): void => {
  // Click-during-rerun: the prior pass's render is on screen but a
  // new pass is in flight. Forwarding the click to the worker would
  // race the next pass's `clickHandlers` map (which doesn't yet
  // contain the registration). Drop the worker-side hand-off but
  // still emit the DOM `lucent-click` event so host-page analytics
  // see the gesture, and brand a transient `data-superseded` on
  // the clicked element so the user knows it registered-then-
  // superseded.
  if (rerunning.value) {
    const target = event?.currentTarget as HTMLElement | undefined;
    if (target && typeof target.setAttribute === "function") {
      target.setAttribute("data-superseded", "true");
      setTimeout(() => target.removeAttribute("data-superseded"), 150);
    }
  } else {
    runner?.notifyClick(id, datum, rowIndex);
  }
  emit("click", {
    targetId: id,
    datum,
    insightId: props.insightId,
  });
};

/**
 * Per-chart error from the 8-code lucent-error taxonomy. Forwarded
 * as a Vue `error` event with the typed shape — defineCustomElement
 * translates to a DOM `lucent-error` CustomEvent on the wrapper
 * element, so embedders can render brand-themed migration banners
 * off `event.detail.code`.
 *
 * Round-2 plan #2. Per-op chart errors no longer terminate inside
 * the Shadow DOM — they reach the host page with structured detail.
 */
/**
 * `sbi.ui.accordion` toggle forwarded from the tree. Carries the
 * accordion's `summary` and the new `open` state. Translated to a
 * DOM `accordion-toggle` CustomEvent by defineCustomElement.
 */
const onAccordionToggle = (summary: string, open: boolean): void => {
  emit("accordion-toggle", { summary, open, insightId: props.insightId });
};

/**
 * `sbi.ui.callout` mount forwarded from the tree. Fires once per Vue
 * mount of the callout — re-renders that just patch props don't fire
 * again. Translated to a DOM `callout-rendered` CustomEvent.
 */
const onCalloutRendered = (kind: string, title?: string): void => {
  emit("callout-rendered", { kind, title, insightId: props.insightId });
};

const onChartError = (payload: {
  code: string;
  message: string;
  chartType: string;
  targetId?: string;
}): void => {
  emit("error", {
    code: payload.code,
    message: payload.message,
    insightId: props.insightId,
    instancePath: payload.targetId ? `/chart/${payload.targetId}` : undefined,
  });
};

const onChangeTarget = (id: string, value: ChangeValue): void => {
  runner?.notifyChange(id, value);
};

/**
 * Chrome-click dispatch — modal-drilldown X / Esc / backdrop. Sister
 * path to `onClickTarget` but WITHOUT the `rerunning` supersede gate
 * and WITHOUT the `data-superseded` shimmer: closing a modal is host
 * chrome, not author-data interaction, and the user expects it to
 * always work even mid-rerun. The downstream effect (`onClose` calls
 * `setSelected(null)` → schedules the next pass) supersedes whatever
 * was in flight, which is the desired behaviour.
 *
 * No DOM `click` event is emitted here — chrome interactions are
 * internal to the embed; embedder analytics tracking author clicks
 * shouldn't see modal-close events.
 */
const onChromeClickTarget = (id: string): void => {
  runner?.notifyClick(id);
};

/**
 * Strip filter nodes flagged `hideOnEmbed` from the root tree when
 * the insight is rendered outside the Lucent dashboard (i.e. via a
 * client-service-key-minted token in a customer product). Runs before
 * `groupTopLevelTree` so the filter-bar grouping sees the post-prune
 * count — three filters with one hidden collapse to a 2-filter
 * sub-threshold render in the embed instead of a 3-filter bar
 * stylised around two surviving controls.
 *
 * Two shapes get pruned:
 *
 *   1. Flat root-level filter primitives — dropped outright.
 *   2. Root-level `columns` rows whose cells each hold a single
 *      filter primitive — the cell holding the hidden filter is
 *      dropped, the `total` (logical span) is reduced to match, and
 *      if zero cells remain the columns node itself disappears.
 *
 * Filters nested deeper (inside a `section`, `accordion`, `tab`,
 * `if`, `drilldown`, etc.) are intentionally NOT pruned at this
 * layer — the author chose a container, and per-author-discretion
 * pruning inside arbitrary subtrees would surprise more than it
 * helps. Per-filter components inside those containers can opt into
 * a `useIsLucentDashboard()` check on their own if needed.
 *
 * The worker still emits the filter op and still seeds the filter's
 * default value into state, so queries that read the filter's value
 * observe it normally — only the visible control is omitted.
 */
const pruneHideOnEmbed = (nodes: readonly TreeNode[]): TreeNode[] => {
  const out: TreeNode[] = [];
  for (const node of nodes) {
    if (FILTER_KINDS.has(node.kind)) {
      const opts = (node as { opts?: { hideOnEmbed?: boolean } }).opts;
      if (opts?.hideOnEmbed === true) continue;
      out.push(node);
      continue;
    }
    if (node.kind === "columns") {
      const isFilterRow =
        node.cells.length > 0 &&
        node.cells.every(
          (c) =>
            c.children.length === 1 && FILTER_KINDS.has(c.children[0]!.kind),
        );
      if (isFilterRow) {
        const keptCells = node.cells.filter((c) => {
          const child = c.children[0]!;
          const opts = (child as { opts?: { hideOnEmbed?: boolean } }).opts;
          return opts?.hideOnEmbed !== true;
        });
        if (keptCells.length === 0) continue;
        out.push({
          ...node,
          cells: keptCells,
          total: keptCells.reduce((acc, c) => acc + c.span, 0),
        });
        continue;
      }
    }
    out.push(node);
  }
  return out;
};

/*
 * Top-level filter-bar grouping. See `filter-bar-grouping.ts` for the
 * detection rules. The computed maps the current root tree into a
 * sequence of either single nodes or "bar" runs of 3+ consecutive
 * flat filter primitives, which the template renders as a stylised
 * `.lucent-filters-bar` container around the existing per-filter
 * components.
 *
 * `pruneHideOnEmbed` strips dashboard-only filter controls first so
 * the bar threshold is evaluated against what will actually render.
 */
const groupedTree = computed<RootGroup[]>(() => {
  const pruned = isLucentDashboard.value
    ? tree.value
    : pruneHideOnEmbed(tree.value);
  return groupTopLevelTree(pruned);
});

// Public + test-only surface. `setState` is the documented embed API
// (the Web Component proxies to it). The `__test*` entries are
// internal — they let `tests/embed/element/lucent-insight.test.ts`
// drive the chart-event forwarders without traversing through child
// components, which is fragile in happy-dom under Vue 3 `<script
// setup>` (child `vm.$emit` doesn't always reach a v-for-bound
// parent listener in the test harness).
defineExpose({
  /**
   * Shallow-merge `state` into the `host` half of the namespaced
   * viewer state. `undefined` deletes a key; `null` writes null.
   * Multiple calls within one animation frame coalesce into a
   * single rerun.
   */
  setState(state: Record<string, unknown>): void {
    runner?.setState(state);
  },
  /**
   * Replace the `host` half of the namespaced viewer state
   * wholesale. Useful for a global Reset.
   */
  replaceState(state: Record<string, unknown>): void {
    runner?.replaceState(state);
  },
  /**
   * Snapshot of keys author code declared via `sbi.state(k, …)`
   * during the most recently committed pass. The dashboard-app's
   * save-as-view handler uses this to filter the persisted `author`
   * state against the live insight's declared surface — keys whose
   * `sbi.state(...)` declaration was removed in a later revision
   * get dropped before the view row is created. Empty array until
   * the first commit lands.
   */
  declaredStateKeys(): ReadonlyArray<string> {
    return runner?.getDeclaredStateKeys() ?? [];
  },
  __testOnClickTarget(
    id: string,
    datum?: Record<string, unknown>,
    rowIndex?: number,
  ): void {
    onClickTarget(id, datum, rowIndex);
  },
  __testOnChartError(payload: {
    code: string;
    message: string;
    chartType: string;
    targetId?: string;
  }): void {
    onChartError(payload);
  },
  __testOnDiagnostic(code: string, detail: Record<string, unknown>): void {
    onDiagnostic(code, detail);
  },
});

onMounted(() => {
  for (const ev of FULLSCREEN_EVENTS) document.addEventListener(ev, onFullscreenChange);
  void startRunner();
});

onBeforeUnmount(() => {
  for (const ev of FULLSCREEN_EVENTS) document.removeEventListener(ev, onFullscreenChange);
  runner?.destroy();
  runner = null;
});

// Re-mount on prop changes — the consumer can swap the insight id or
// token live (e.g. after a re-mint) and we'll pick up cleanly.
watch(
  () => [props.insightId, props.token, props.apiUrl],
  () => {
    runner?.destroy();
    runner = null;
    void startRunner();
  },
);

/**
 * Tab-switch handler. Updates the currently active chrome tab.
 */
const setTab = (next: ChromeTab, ev?: Event): void => {
  activeTab.value = next;
  // The strip scrolls sideways on narrow embeds; keep the chosen tab
  // fully in view.
  (ev?.currentTarget as HTMLElement | null)?.scrollIntoView?.({
    block: "nearest",
    inline: "nearest",
  });
};
</script>

<template>
  <div
    ref="rootEl"
    class="lucent-insight-host"
    :class="{ 'lucent-insight-host--print': print, 'lucent-insight-host--narrow': hostNarrow }"
    :data-phase="phase"
  >
    <div v-if="errorMessage" class="lucent-insight-error">
      <slot name="error" :message="errorMessage">
        <p>{{ errorMessage }}</p>
      </slot>
    </div>

    <div
      v-else-if="phase === 'booting'"
      class="lucent-insight-booting"
      aria-busy="true"
      aria-live="polite"
    >
      <span class="visually-hidden">Connecting…</span>
      <LucentSpinner :size="32" />
    </div>

    <template v-else>
      <!--
        Chrome toolbar — opt-in via `toolbar="true"` on the host
        element. The tab strip on the left collapses to nothing when
        no debug flags are on (a single non-clickable "Insight" pill
        reads as broken UI), but the toolbar itself stays mounted so
        the fullscreen button on the right is always reachable while
        toolbar mode is on. Per docs/plans/mini/fullscreen-embed.md
        "Always visible" decision — applied only inside the opt-in
        branch.

        Chrome-free is the default so marketing-hero / thumbnail /
        host-page-driven-fullscreen embeds get a clean canvas
        without opt-out work. The dashboard and any embedder that
        wants the debug tabs + fullscreen affordance must explicitly
        pass `toolbar="true"`.
      -->
      <div v-if="toolbarEnabled && (hasExtraTabs || fullscreenSupported)" class="chrome-toolbar">
        <div class="chrome-tabs" role="tablist">
          <button
            v-if="hasExtraTabs"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'insight'"
            :class="{ active: activeTab === 'insight' }"
            @click="setTab('insight', $event)"
          >
            Insight
          </button>
          <button
            v-if="showQueries"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'queries'"
            :class="{ active: activeTab === 'queries' }"
            @click="setTab('queries', $event)"
          >
            Queries
            <span v-if="queries.length" class="chrome-tab-count">{{ queries.length }}</span>
          </button>
          <button
            v-if="showVersions"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'versions'"
            :class="{ active: activeTab === 'versions' }"
            @click="setTab('versions', $event)"
          >
            Versions
          </button>
          <button
            v-if="showCode"
            type="button"
            role="tab"
            :aria-selected="activeTab === 'code'"
            :class="{ active: activeTab === 'code' }"
            @click="setTab('code', $event)"
          >
            Code
          </button>
        </div>
        <button
          v-if="fullscreenSupported"
          type="button"
          class="chrome-fullscreen"
          :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
          :title="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
          :aria-pressed="isFullscreen"
          @click="toggleFullscreen"
        >
          <!--
            Two glyphs swapped via v-if: expand-corners when not in
            fullscreen, collapse-corners when in. Stroke-only +
            currentColor so the icon picks up the muted-tab palette
            and matches hover/focus states without per-icon overrides.
          -->
          <svg
            v-if="!isFullscreen"
            viewBox="0 0 24 24"
            aria-hidden="true"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="4 9 4 4 9 4" />
            <polyline points="20 9 20 4 15 4" />
            <polyline points="4 15 4 20 9 20" />
            <polyline points="20 15 20 20 15 20" />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            aria-hidden="true"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 4 9 9 4 9" />
            <polyline points="15 4 15 9 20 9" />
            <polyline points="9 20 9 15 4 15" />
            <polyline points="15 20 15 15 20 15" />
          </svg>
        </button>
      </div>

      <div
        v-show="activeTab === 'insight'"
        class="chrome-tab-body"
        role="tabpanel"
      >
        <template v-for="(group, gIdx) in groupedTree" :key="gIdx">
          <!--
            Filter-bar group: 3+ consecutive flat filter primitives at the
            root were collapsed by `groupedTree`. The wrapper element
            carries the styled surface (background, hairline border,
            rounded corners) and overrides the host's vertical-stack
            layout to a wrapping horizontal flex row so labelled filter
            controls align side by side. Existing per-filter components
            render unchanged inside.
          -->
          <div
            v-if="group.kind === 'bar'"
            class="lucent-filters-bar"
            role="toolbar"
            aria-label="Filters"
          >
            <InsightNode
              v-for="(node, idx) in group.nodes"
              :key="idx"
              :node="node"
              :loading="rerunning"
              :formats="formats"
              @click-target="onClickTarget"
              @change-target="onChangeTarget"
              @chrome-click-target="onChromeClickTarget"
              @diagnostic="onDiagnostic"
              @chart-error="onChartError"
              @accordion-toggle="onAccordionToggle"
              @callout-rendered="onCalloutRendered"
            />
          </div>
          <InsightNode
            v-else
            :node="group.node"
            :loading="rerunning"
            :formats="formats"
            @click-target="onClickTarget"
            @change-target="onChangeTarget"
            @chrome-click-target="onChromeClickTarget"
            @diagnostic="onDiagnostic"
            @chart-error="onChartError"
            @accordion-toggle="onAccordionToggle"
            @callout-rendered="onCalloutRendered"
          />
        </template>
      </div>

      <EmbedHostQueriesTab
        v-if="showQueries"
        v-show="activeTab === 'queries'"
        :queries="queries"
      />

      <EmbedHostVersionsTab
        v-if="showVersions"
        v-show="activeTab === 'versions'"
        :api-url="apiUrl"
        :insight-id="insightId"
        :token="token"
        :active="activeTab === 'versions'"
      />

      <EmbedHostCodeTab
        v-if="showCode && activeTab === 'code'"
        :source-code="sourceCode"
        :api-version="sourceApiVersion"
        :version="sourceVersion"
      />
    </template>
  </div>
</template>

<style scoped>
/* Custom elements default to `display: inline`, which gives the shadow
 * root a shrink-to-fit containing block. ECharts' `autoresize`
 * measures its parent's rendered width on mount — if that width
 * collapses (e.g. a single chart with no sibling content to inflate
 * the column), ECharts falls back to its internal 100px default and
 * the chart renders at the wrong size. Forcing the host to `block`
 * + giving the inner flex chain `width: 100%; min-width: 0` keeps
 * the available-width signal intact all the way down to the chart. */
:host {
  display: block;

  /* ── Designer-tier theming tokens ──────────────────────────────────
   * Embedders override these on the `<lucent-insight>` element (or any
   * ancestor) to restyle interior elements without reaching into the
   * shadow root. Defaults below preserve the embed's stock look — a
   * theme that sets none of them gets the same render as before. See
   * `docs/insights-api/embed-css-tokens.md` for the full reference.
   */

  /* Typography — CSS `font` shorthand (weight size/line family). */
  --lucent-font-label:         600 11px/1.2 system-ui, sans-serif;
  --lucent-font-value:         600 calc(30px * var(--lucent-font-scale, 1))/1.1 system-ui, sans-serif;
  --lucent-font-table-cell:    400 13px/1.5 system-ui, sans-serif;
  --lucent-font-table-header:  500 10px/1.3 ui-monospace, SFMono-Regular, Menlo, monospace;

  /* Tracking + case (CSS `font` shorthand can't carry these). */
  --lucent-tracking-label:         0.06em;
  --lucent-tracking-value:         -0.01em;
  --lucent-tracking-section-title: -0.015em;
  --lucent-tracking-table-header:  0.12em;
  --lucent-case-label:             uppercase;
  --lucent-case-section-title:     none;
  --lucent-case-table-header:      uppercase;

  /* Density + section-title font are deliberately NOT declared here.
   * A `:host` declaration would beat a value the embedder sets on an
   * ancestor and kill every component's width-aware fallback. Their
   * defaults are the private `--_lucent-*` properties on
   * `.lucent-insight-host` below, which shrink on narrow embeds;
   * components read `var(--lucent-X, var(--_lucent-X, <default>))`.
   * A token set by the embedder wins at every width. */

  /* Spacing scale + section internal gap. These were previously
     referenced bare (e.g. `gap: var(--space-3)`) without a host-level
     default, which silently broke `gap` / `margin` on any component
     that used them. Defining them here makes the cascade resolve in
     stock themes; themes can still override per scale step.
       --space-3 ≈ 12px (compact, used for body / header internal gaps)
       --space-4 ≈ 16px (looser, used for outer section margins)
       --lucent-ui-section-gap — gap between section header and body. */
  --space-1:                0.25rem;
  --space-2:                0.5rem;
  --space-3:                0.75rem;
  --space-4:                1rem;
  --lucent-ui-section-gap:  var(--space-3);

  /* Table colour. Six tokens that fully control the table chrome
     without cascading into KPI tiles / callouts the way `--rule`
     and `--rule-soft` do. Defaults preserve the stock look: header
     in `--rule-soft` band with muted text, hairline row dividers,
     no zebra, no hover wash. Themes opt into stripe / hover / colour
     by setting these. */
  --lucent-table-header-bg:    var(--rule-soft, #f5f5f5);
  --lucent-table-header-fg:    var(--card-muted, #6a6f88);
  --lucent-table-header-rule:  1px solid var(--rule, #e5e3dc);
  --lucent-table-row-rule:     1px solid var(--rule-soft, #efede6);
  --lucent-table-row-stripe:   transparent;
  --lucent-table-row-hover-bg: transparent;

  /* Card surface. */
  --lucent-card-border:   1px solid var(--rule, #e5e3dc);
  --lucent-card-shadow:   none;

  /* Chart palette + axis/grid + text. All read at chart-options
     build time via `resolveCssVar` / `resolveColour` in
     `echarts-base.ts`, so themes that set these get the values on
     every chart paint. Canvas-safety constraint: colour values must
     be concrete `#hex` / `rgb()` / `rgba()` strings (no nested
     `var(--…)` references), because ECharts paints to canvas which
     can't follow CSS-variable lookups. */
  --lucent-series-palette: #6aceb0, #5b9cf2, #f4a261, #e07b91, #b18cf0, #f3c94a;
  --lucent-chart-axis-color: #bfb8a8;
  --lucent-chart-grid-color: #dcd5c4;
  /* New: chart text + font + per-element label/legend colours +
     formatter case + grid style. Defaults preserve the editorial
     mono / uppercase / warm-charcoal baseline. */
  --lucent-chart-text-color:   #2f2c26;
  --lucent-chart-font-family:  ui-monospace, "JetBrains Mono", "SF Mono", Menlo, Consolas, monospace;
  --lucent-chart-label-color:  #6a655b;
  --lucent-chart-legend-color: #4a4641;
  --lucent-chart-legend-case:  uppercase;
  --lucent-chart-grid-style:   dashed;

  /* Semantic chart accents used by charts that split colour by sign or
     drive a ramp (waterfall's `divergent.{positive,negative}Color`,
     the heatmap `lowColor` / `highColor` / `midColor` fallbacks).
     Previously referenced from those components without a `:host`
     default — `resolveCssVar()` returned undefined and every consumer
     fell through to its hard-coded backup, so the tokens were dead in
     practice. Declaring the defaults here makes author specs like
     `highColor: "var(--lucent-chart-positive)"` actually resolve.
     Canvas-safety: keep these as concrete hex, not nested `var(--…)`. */
  --lucent-chart-positive: #3aa988;
  --lucent-chart-negative: #d96363;
  --lucent-chart-neutral:  #f3f1ea;
}
.lucent-insight-host {
  /* Private defaults behind the public density tokens; narrowed by the
   * `@container lucent-embed` rules in the unscoped block. */
  --_lucent-card-pad: 16px 18px;
  --_lucent-card-pad-primary: 22px 24px 22px 22px;
  --_lucent-table-row-pad: 12px 14px;
  --_lucent-section-gap: 1rem;
  --_lucent-filters-pad: 12px 16px;
  --_lucent-section-title-size: 16px;
  display: flex;
  flex-direction: column;
  gap: var(--lucent-section-gap, var(--_lucent-section-gap, 1rem));
  width: 100%;
  min-width: 0;
  /* Responsive layout keys off the embed's own width, never the
   * viewport — a 360px card on a desktop page is a phone layout.
   * Descendants query `@container lucent-embed (…)` for whole-embed
   * decisions; layout slots (column cells, tab bodies, …) declare
   * their own anonymous containers for local ones. Inline-size
   * containment means the host no longer sizes to its content, so
   * the element needs a width from its parent (see
   * docs/insights-api/embed-css-tokens.md). In print the slot is
   * 180mm ≈ 680px, above every breakpoint. */
  container: lucent-embed / inline-size;
}
/* The host's own gap, narrowed from script (`hostNarrow`): the
 * `@container lucent-embed` rules below only reach its children. Same
 * value as the < 480px rule gives them. */
.lucent-insight-host--narrow {
  --_lucent-section-gap: 0.75rem;
}
.lucent-insight-error {
  padding: 1rem;
  color: var(--lucent-error, #b00020);
}

/* Phase 1 — booting. Generic centered spinner, kept short and
 * neutral; users see this for ~50–500 ms while the Worker boots. */
.lucent-insight-booting {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 96px;
  padding: 1rem;
}

/* Click-during-rerun affordance. When the user clicks a target
 * while a pass is mid-flight, the runner drops the hand-off to the
 * worker (it would race the new pass's handler map) and tags the
 * clicked element so the user gets a brief visual acknowledgement
 * — "we saw your click; it was superseded by the rerun in flight"
 * — rather than a silent no-op. Removed after 150ms by EmbedHost. */
[data-superseded="true"] {
  cursor: progress;
  outline: 2px solid var(--rule-soft, rgba(0, 0, 0, 0.12));
  outline-offset: 2px;
  animation: lucent-superseded-shimmer 150ms ease-out;
}
@keyframes lucent-superseded-shimmer {
  0%   { opacity: 1; }
  50%  { opacity: 0.6; }
  100% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  [data-superseded="true"] {
    animation: none;
    opacity: 0.7;
  }
}
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
/* ---------- Chrome toolbar ----------
 *
 * Always-on horizontal strip sitting above the insight body. Left
 * cluster is the existing tab list (Insight + optional Queries /
 * Versions / Code); right cluster is the fullscreen toggle. The
 * hairline rule moved off `.chrome-tabs` onto this wrapper so the
 * underline spans the full row even when the tab list collapses to
 * a single label.
 *
 * The rule is an inset shadow, not a border: the tab strip scrolls
 * sideways on narrow embeds, and an `overflow-x` box clips the old
 * `margin-bottom: -1px` overlap trick. The active tab's 2px border
 * paints over the parent's inset shadow instead. */
.chrome-toolbar {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 4px;
  min-width: 0;
  box-shadow: inset 0 -1px 0 var(--rule, #e5e7eb);
  margin-bottom: 4px;
}

/* ---------- Tab strip ----------
 *
 * Mirrors `InsightTabs.vue`'s tab-strip styling so the chrome reads as
 * "part of the embed" rather than a foreign layer. The border + margin
 * live on `.chrome-toolbar` above; this row just lays out the tab
 * buttons. */
.chrome-tabs {
  display: flex;
  gap: 2px;
  /* Scroll, never wrap or overflow: four tabs + the fullscreen
   * button need ~370px, more than a phone-width embed has. */
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}
.chrome-tabs::-webkit-scrollbar {
  display: none;
}
.chrome-tabs button {
  flex: 0 0 auto;
  white-space: nowrap;
  touch-action: manipulation;
  appearance: none;
  background: transparent;
  border: none;
  padding: 10px 16px;
  font-family: var(--font, system-ui, sans-serif);
  font-size: 13px;
  font-weight: 500;
  color: var(--card-muted, #6b7280);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition:
    color 120ms,
    border-color 120ms;
}
@container lucent-embed (max-width: 479px) {
  .chrome-tabs button {
    padding: 10px;
  }
  .chrome-tab-count {
    padding: 0 4px;
  }
}
.chrome-tabs button:hover {
  color: var(--card-ink, #1a1a1a);
}
.chrome-tabs button.active {
  color: var(--card-ink, #1a1a1a);
  border-bottom-color: var(--primary, #0777b3);
  font-weight: 600;
}
.chrome-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: var(--rule-soft, rgba(0, 0, 0, 0.06));
  font-family: var(--mono, ui-monospace, monospace);
  font-size: 10.5px;
  font-weight: 600;
  color: var(--card-muted, #6b7280);
}
.chrome-tab-body {
  display: flex;
  flex-direction: column;
  gap: var(--lucent-section-gap, var(--_lucent-section-gap, 1rem));
  min-width: 0;
}

/* ---------- Fullscreen toggle ----------
 *
 * Icon-only button on the right of the chrome toolbar. Click promotes
 * the `<lucent-insight>` host element via the Fullscreen API; Escape
 * exits natively. State (icon glyph, aria-label, aria-pressed) is
 * driven from `document.fullscreenElement === host`, so Escape exits
 * stay in sync without our click handler running. */
.chrome-fullscreen {
  flex: 0 0 auto;
  appearance: none;
  background: transparent;
  border: none;
  padding: 8px 10px;
  color: var(--card-muted, #6b7280);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: color 120ms, background 120ms;
}
.chrome-fullscreen:hover {
  color: var(--card-ink, #1a1a1a);
  background: var(--rule-soft, rgba(0, 0, 0, 0.06));
}
.chrome-fullscreen:focus-visible {
  outline: 2px solid var(--primary, #0777b3);
  outline-offset: 2px;
}
@media (pointer: coarse) {
  .chrome-tabs button {
    min-height: 44px;
  }
  .chrome-fullscreen {
    min-width: 44px;
    min-height: 44px;
  }
}

/* ---------- Top-level filters bar ----------
 *
 * Rendered around a run of 3+ consecutive flat filter primitives at
 * the root of the tree (see `groupedTree` in <script> +
 * `filter-bar-grouping.ts`). Soft surface + hairline border gives the
 * strip an obvious "controls live here" affordance without competing
 * with the report content beneath. The children are the same
 * InsightSelect / InsightDateRange / etc. components used inline
 * elsewhere — we override the host's vertical stack with a wrapping
 * horizontal flex row and let each filter's label-above-control sit
 * at its natural width. On narrower embeds the filters grow to share
 * each line (480–639px), then stack full width (< 480px). */
.lucent-filters-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 16px;
  padding: var(--lucent-filters-bar-pad, var(--_lucent-filters-pad, 12px 16px));
  background: var(--rule-soft, rgba(0, 0, 0, 0.03));
  border: 1px solid var(--rule, #e5e7eb);
  border-radius: var(--radius, 8px);
  min-width: 0;
}
/* Each child is a per-filter component root. They default to inline-flex
 * column (label above control); inside the bar we just want them to
 * sit on the row without being stretched by the flex container. */
.lucent-filters-bar > :deep(*) {
  flex: 0 1 auto;
  min-width: 0;
}
@container lucent-embed (max-width: 639px) {
  .lucent-filters-bar {
    gap: 10px 12px;
  }
  .lucent-filters-bar > :deep(*) {
    flex: 1 1 12rem;
    max-width: 100%;
  }
}
@container lucent-embed (max-width: 479px) {
  .lucent-filters-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .lucent-filters-bar > :deep(*) {
    flex: 0 0 auto;
    width: 100%;
  }
}

/* The Queries, Code and Versions panels' own styles live in their respective sub-components. */
</style>

<!--
  Print-mode stylesheet.

  Non-scoped block so the rules apply across the entire shadow root,
  including descendant components. Vue's defineCustomElement bundles
  every <style> block from the SFC into the shadow root regardless of
  the `scoped` attribute — that's the route by which print rules
  reach KPI tiles, charts, tables, etc. nested inside InsightNode.

  Every rule is gated by `.lucent-insight-host--print` (applied to
  EmbedHost's root element only when `props.print === true`) so the
  block is inert on screen.

  Anything cross-cutting that fixes a "this looks fine on screen but
  breaks in a board-pack PDF" issue belongs here. Per-component
  print branches (interactive controls flipping to static text, tabs
  stacking, KPI info button hiding) stay in their respective
  components — those are about the component's identity in print, not
  shared page geometry.
-->
<style>
/* ---------- Narrow-embed density ----------
 *
 * Tightens the private density defaults when the WHOLE embed is
 * narrow (named container, so a 4-up KPI row on a desktop keeps its
 * padding). A container query can't restyle the container itself, so
 * the values land on its children and inherit from there — including
 * into top-layer dialogs, which are still DOM descendants. Never
 * matches in print: the PDF slot is ~680px. */
@container lucent-embed (max-width: 639px) {
  .lucent-insight-host > * {
    --_lucent-filters-pad: 10px 12px;
  }
}
@container lucent-embed (max-width: 479px) {
  .lucent-insight-host > * {
    --_lucent-card-pad: 12px 14px;
    --_lucent-card-pad-primary: 12px 14px;
    --_lucent-table-row-pad: 8px 10px;
    --_lucent-section-gap: 0.75rem;
    --_lucent-filters-pad: 10px;
    --_lucent-section-title-size: 15px;
  }
}

/* ---------- :host(:fullscreen) ----------
 *
 * When the user promotes the `<lucent-insight>` element via the
 * Fullscreen API, the shadow root's containing block becomes the
 * viewport. Three adjustments:
 *
 *   1. Paint a paper-coloured surface inside the host so the embed
 *      visually matches whatever page promoted it. `--paper` is the
 *      same token the dashboard uses for its body background, and
 *      it inherits through the shadow boundary, so light/dark mode
 *      tracks the host page automatically. Without this we fall
 *      back to the user-agent default (`::backdrop` black) which
 *      clashes with every embed palette.
 *   2. `::backdrop` belt-and-braces — some browsers paint the
 *      backdrop above the host's own background briefly during the
 *      transition. Match it to `--paper` so there's no flash.
 *   3. Generous padding (24px) so the report doesn't run to the
 *      bezel on a boardroom projector.
 *   4. Auto-bump `--lucent-font-scale` to 1.4. KPI value sizes and
 *      anything else reading the variable then scale up so a board-
 *      room projector at 4 m sightline lands the support tiles in
 *      legible range. Default (non-fullscreen) value of `1` comes
 *      from the consumer-side `var(..., 1)` fallback in InsightKpi.
 *
 * This rule lives in the *unscoped* style block on purpose: Vue's
 * `<style scoped>` post-processor appends `[data-v-hash]` to
 * `:host(...)` selectors, but the shadow host element (the
 * `<lucent-insight>` custom element) doesn't carry that attribute
 * — only Vue-rendered descendants inside the shadow do — so a
 * scoped `:host(:fullscreen)` rule silently never matches. Keeping
 * the rule unscoped sidesteps that.
 *
 * `-webkit-full-screen` is Safari's prefixed selector — the standard
 * `:fullscreen` doesn't match in Safari versions still on the
 * prefixed implementation. Browsers ignore unknown selectors in a
 * selector list rather than dropping the whole rule, so this pairing
 * is safe.
 *
 * Gated on `[toolbar]` attribute presence. Fullscreen is paired with
 * the toolbar opt-in (the only embed-side fullscreen trigger lives in
 * the toolbar) — when an embedder explicitly disables the toolbar,
 * fullscreen behaviour goes with it. If a host page calls
 * `element.requestFullscreen()` programmatically on a toolbar-less
 * embed, the browser still promotes the element, but none of the
 * boardroom-projection styling engages: no `--paper` background, no
 * 24px bezel padding, no `--lucent-font-scale` bump. The viewer sees
 * the embed at its normal size, on a user-agent-default backdrop —
 * fine for "Escape to exit", not fine as a curated boardroom
 * presentation, which is the explicit feature being gated.
 */
:host([toolbar]:fullscreen),
:host([toolbar]:-webkit-full-screen) {
  background: var(--paper, #f7f6f2);
  color: var(--card-ink, #0e0e10);
  /* Notch / home-indicator safe areas on tablets in landscape. */
  padding:
    max(env(safe-area-inset-top), clamp(12px, 3vw, 24px))
    max(env(safe-area-inset-right), clamp(12px, 3vw, 24px))
    max(env(safe-area-inset-bottom), clamp(12px, 3vw, 24px))
    max(env(safe-area-inset-left), clamp(12px, 3vw, 24px));
  overflow: auto;
  --lucent-font-scale: 1.4;
}
/* Projector scale is too much on a small screen. Viewport query on
 * purpose: in fullscreen the host IS the viewport. */
@media (max-width: 639px) {
  :host([toolbar]:fullscreen),
  :host([toolbar]:-webkit-full-screen) {
    --lucent-font-scale: 1.15;
  }
}
:host([toolbar]:fullscreen)::backdrop,
:host([toolbar]:-webkit-full-screen)::backdrop {
  background: var(--paper, #f7f6f2);
}

/*
 * Designed-report print stylesheet.
 *
 * Goal: the printed insight reads as an annual-report spread a
 * design team would ship — confident colour, real card chrome,
 * sans-serif headings, mono reserved for numeric values where
 * column alignment actually matters. The earlier "Tufte hairline"
 * pass collapsed everything to mono labels + thin rules, which
 * read as a data dump.
 *
 * Rules every selector follows:
 *   - Sans-serif (Geist) for headings, labels, body copy. Mono
 *     ONLY for numeric / tabular values where digits need to
 *     align by column.
 *   - Section headings are titles, not tracked-up tags. Title
 *     case, real heading sizes, restrained tracking.
 *   - KPI tiles carry actual card chrome — soft white surface,
 *     hairline border, generous padding, accent left rule on
 *     the primary tile.
 *   - Mint (--mint) used as a deliberate brand accent in two
 *     surfaces only: primary KPI tint, and the diamond glyph on
 *     section titles / chart eyebrows. Not as a hairline-painting
 *     palette.
 *   - Generous vertical rhythm; one body line-height stack so
 *     scanned columns of text feel intentional.
 */

/* ---------- Tone & layout primitives ---------- */
.lucent-insight-host--print {
  --paper: #f7f6f2;       /* warm-paper canvas */
  --card: #ffffff;        /* tile surface */
  --card-ink: #0a0e1c;
  --card-muted: #6a6f88;
  --color-text-muted: #6a6f88;
  --rule: #e5e3dc;        /* hairline rules — warm, not cold grey */
  --rule-soft: #efede6;
  --mint: #6aceb0;
  --mint-deep: #3aa988;
  --mint-wash: #f0faf6;   /* primary-KPI tint */
  --mint-wash-edge: #cdebde;
  line-height: 1.5;
  font-family: "Geist", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: var(--card-ink);
}

/* Chart fit: clamp ECharts wrappers to the printable width. */
.lucent-insight-host--print .chart,
.lucent-insight-host--print .echarts {
  max-width: 100% !important;
  width: 100% !important;
  overflow: hidden !important;
}

/* Page-break hygiene. */
.lucent-insight-host--print .chart,
.lucent-insight-host--print .echarts,
.lucent-insight-host--print .chart-shell,
.lucent-insight-host--print .kpi {
  break-inside: avoid-page;
}
.lucent-insight-host--print table { break-inside: auto; }
.lucent-insight-host--print tr { break-inside: avoid-page; }

/* Debug chrome: defence in depth. */
.lucent-insight-host--print .chrome-tabs { display: none !important; }

/* Accordions force-open with no chevron; the open attr is set in
 * InsightAccordion.vue when isPrint() is true. */
.lucent-insight-host--print details summary {
  cursor: default;
  list-style: none;
}
.lucent-insight-host--print details summary::-webkit-details-marker {
  display: none;
}
.lucent-insight-host--print details summary::marker { content: ""; }

/* ---------- KPI tiles ----------
 * Real card surfaces. White tile on warm paper, soft warm hairline
 * border, generous padding. Label in sans (small uppercase, subtle
 * letter-spacing — editorial, not tracked-out mono). Value in
 * Geist with tabular figures so digits align across siblings.
 * Primary KPI gets a mint-tinted background and a heavier accent
 * rule, anchoring it as the report's headline statistic. */
.lucent-insight-host--print .kpi {
  background: var(--card) !important;
  border: 1px solid var(--rule) !important;
  border-radius: 8px !important;
  padding: 18px 22px !important;
  min-height: 0 !important;
  page-break-inside: avoid;
}
.lucent-insight-host--print .kpi .label {
  font-family: "Geist", system-ui, sans-serif !important;
  font-size: 9.5pt !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em !important;
  color: var(--card-muted) !important;
  text-transform: uppercase !important;
  margin-bottom: 10px !important;
}
.lucent-insight-host--print .kpi .value {
  font-family: "Geist", system-ui, sans-serif !important;
  font-size: 30pt !important;
  font-weight: 700 !important;
  letter-spacing: -0.025em !important;
  line-height: 1.02 !important;
  color: var(--card-ink) !important;
  font-variant-numeric: tabular-nums;
}
.lucent-insight-host--print .kpi .caption {
  font-size: 10.5pt !important;
  color: var(--card-muted) !important;
  margin-top: 8px !important;
}
.lucent-insight-host--print .kpi.kpi--primary {
  background: linear-gradient(180deg, var(--mint-wash) 0%, var(--card) 100%) !important;
  border: 1px solid var(--mint-wash-edge) !important;
  border-left: 4px solid var(--mint) !important;
  padding-left: 22px !important;
}
.lucent-insight-host--print .kpi.kpi--primary .label {
  color: var(--mint-deep) !important;
}
.lucent-insight-host--print .kpi.kpi--primary .value {
  color: #103b2c !important;
  font-size: 40pt !important;
}
.lucent-insight-host--print .kpi .delta {
  font-family: "Geist", system-ui, sans-serif !important;
  font-size: 10.5pt !important;
  font-weight: 600 !important;
  color: var(--card-muted) !important;
}
.lucent-insight-host--print .kpi .delta-up   { color: var(--mint-deep) !important; }
.lucent-insight-host--print .kpi .delta-down { color: #c1442e !important; }

/* ---------- Charts ----------
 * Card surface to match the KPI treatment — the embed reads as a
 * coordinated grid of designed components rather than a sequence
 * of disembodied figures. Title is a proper sentence-case heading
 * in sans-serif, marked by a mint diamond glyph. */
.lucent-insight-host--print .chart-shell {
  background: var(--card) !important;
  border: 1px solid var(--rule) !important;
  border-radius: 8px !important;
  padding: 20px 22px 16px !important;
  margin: 18px 0 22px !important;
  overflow: hidden;
}
.lucent-insight-host--print .chart-title {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  padding: 0 0 12px !important;
  margin: 0 0 14px !important;
  border-bottom: 1px solid var(--rule-soft) !important;
  font-family: "Geist", system-ui, sans-serif !important;
  font-size: 13pt !important;
  font-weight: 600 !important;
  letter-spacing: -0.005em !important;
  color: var(--card-ink) !important;
  text-transform: none !important;
  height: auto !important;
  display: flex !important;
  align-items: center;
  gap: 10px;
}
.lucent-insight-host--print .chart-title-mark {
  color: var(--mint) !important;
  font-size: 9pt;
  line-height: 1;
}
.lucent-insight-host--print .chart {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
}
.lucent-insight-host--print .chart-placeholder,
.lucent-insight-host--print .chart-loading {
  background: transparent !important;
  border: 0 !important;
  color: var(--card-muted) !important;
}

/* ---------- Tables ----------
 * Soft-card table to match KPIs and charts: white surface, hairline
 * border, rounded corners. Sans header in small-caps weight, mint
 * underline rule on the header, alternating rows for legibility,
 * mono ONLY for numeric columns where digit alignment matters. */
.lucent-insight-host--print .table-wrap {
  background: var(--card) !important;
  border: 1px solid var(--rule) !important;
  border-radius: 8px !important;
  overflow: hidden !important;
  margin: 14px 0 18px !important;
}
.lucent-insight-host--print table {
  max-width: 100% !important;
  table-layout: auto;
  border-collapse: collapse;
  width: 100%;
}
.lucent-insight-host--print thead th {
  background: var(--rule-soft) !important;
  color: var(--card-ink) !important;
  font-family: "Geist", system-ui, sans-serif !important;
  font-size: 9.5pt !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em !important;
  text-transform: uppercase !important;
  border: 0 !important;
  border-bottom: 2px solid var(--mint) !important;
  padding: 10px 14px !important;
  text-align: left !important;
}
.lucent-insight-host--print tbody td {
  padding: 9px 14px !important;
  border: 0 !important;
  border-bottom: 1px solid var(--rule-soft) !important;
  color: var(--card-ink) !important;
  font-size: 10.5pt;
  vertical-align: top;
  overflow-wrap: anywhere;
}
/* Paper can't scroll sideways and the wrap clips (overflow: hidden
   above), so let every text cell wrap — the screen rule's nowrap
   otherwise wins over the wrapping and wide tables lose their right
   edge. Numbers stay on one line so digits align. */
.lucent-insight-host--print thead th,
.lucent-insight-host--print tbody td {
  white-space: normal !important;
}
.lucent-insight-host--print th.num,
.lucent-insight-host--print td.num {
  white-space: nowrap !important;
}
/* Many columns: tighten type and padding so the table fits 180mm. */
.lucent-insight-host--print .table-wrap.is-dense table {
  font-size: 8.5pt;
}
.lucent-insight-host--print .table-wrap.is-dense thead th,
.lucent-insight-host--print .table-wrap.is-dense tbody td {
  padding: 6px 8px !important;
}
.lucent-insight-host--print .table-wrap.is-dense tbody td {
  font-size: 8.5pt;
}
.lucent-insight-host--print .sort-indicator {
  display: none !important;
}
.lucent-insight-host--print tbody tr:nth-child(even) td {
  background: rgba(247, 246, 242, 0.55) !important;
}
.lucent-insight-host--print tbody tr:last-child td {
  border-bottom: 0 !important;
}
.lucent-insight-host--print th.num,
.lucent-insight-host--print td.num {
  text-align: right !important;
  font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, monospace !important;
  font-feature-settings: "tnum";
}
.lucent-insight-host--print tbody td a {
  color: var(--card-ink) !important;
  text-decoration: none !important;
}
.lucent-insight-host--print .table-overflow {
  color: var(--card-muted) !important;
  font-style: italic;
  font-size: 9.5pt;
  background: var(--rule-soft) !important;
  border-top: 1px solid var(--rule-soft) !important;
}

/* ---------- Sections ----------
 * Real magazine-style heading: sentence case, sans-serif, mid-size.
 * A short mint underline anchor below the title gives it a designed
 * feel without resorting to a tracked-out mono tag. */
.lucent-insight-host--print .section { margin-top: 36px !important; }
.lucent-insight-host--print .section:first-child { margin-top: 0 !important; }
.lucent-insight-host--print .section-header {
  border: 0 !important;
  padding-bottom: 0 !important;
  margin-bottom: 18px !important;
}
.lucent-insight-host--print .section-title {
  font-family: "Geist", system-ui, sans-serif !important;
  font-size: 18pt !important;
  font-weight: 700 !important;
  letter-spacing: -0.015em !important;
  color: var(--card-ink) !important;
  text-transform: none !important;
  /* !important: the on-screen rule is block-level flex at equal
     specificity; without this the mint underline spans the page. */
  display: inline-block !important;
  max-width: 100%;
  padding-bottom: 8px;
  border-bottom: 3px solid var(--mint);
}
.lucent-insight-host--print .section-title::before {
  content: none !important;
}

/* ---------- Markdown / text (sbi.ui.text) ----------
 * Body copy in Geist, 11pt, newspaper line-height. Bounded to
 * 70ch so a wide page doesn't stretch lines past comfortable scan
 * distance. Headings, lists, code, blockquote all editorialised. */
.lucent-insight-host--print .text-card {
  background: transparent !important;
  border: 0 !important;
  padding: 0 !important;
  margin: 12px 0 !important;
}
/* On screen markdown tables and code blocks scroll sideways; paper
   can't, so lay them out in full and wrap. */
.lucent-insight-host--print .prose table {
  display: table !important;
  overflow: visible !important;
  width: 100%;
}
.lucent-insight-host--print .prose pre {
  overflow: visible !important;
  white-space: pre-wrap !important;
}
.lucent-insight-host--print .prose pre code {
  white-space: pre-wrap !important;
}
.lucent-insight-host--print .prose {
  font-family: "Geist", system-ui, sans-serif;
  font-size: 11pt;
  line-height: 1.6;
  color: var(--card-ink);
  max-width: 70ch;
}
.lucent-insight-host--print .prose p { margin: 0 0 10px; }
.lucent-insight-host--print .prose h1,
.lucent-insight-host--print .prose h2,
.lucent-insight-host--print .prose h3 {
  font-family: "Geist", system-ui, sans-serif;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 18px 0 6px;
  color: var(--card-ink);
}
.lucent-insight-host--print .prose h1 { font-size: 16pt; }
.lucent-insight-host--print .prose h2 { font-size: 13pt; }
.lucent-insight-host--print .prose h3 {
  font-size: 11pt;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.lucent-insight-host--print .prose ul,
.lucent-insight-host--print .prose ol {
  padding-left: 20px;
  margin: 6px 0 12px;
}
.lucent-insight-host--print .prose li { margin-bottom: 4px; }
.lucent-insight-host--print .prose code {
  font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, monospace;
  font-size: 0.92em;
  background: rgba(106, 206, 176, 0.10);
  padding: 1px 4px;
  border-radius: 2px;
}
.lucent-insight-host--print .prose blockquote {
  margin: 12px 0;
  padding-left: 14px;
  border-left: 3px solid var(--mint);
  color: var(--card-muted);
  font-style: italic;
}

/* ---------- Callouts (sbi.ui.callout) ----------
 * Editorial pull-quote: hairline left rule + italic body, no box. */
.lucent-insight-host--print .callout {
  background: transparent !important;
  border: 0 !important;
  border-left: 3px solid var(--mint) !important;
  border-radius: 0 !important;
  padding: 8px 14px !important;
  margin: 14px 0 !important;
  color: var(--card-ink) !important;
}
.lucent-insight-host--print .callout-icon { display: none !important; }
.lucent-insight-host--print .callout-title {
  font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, monospace !important;
  font-size: 9px !important;
  font-weight: 700 !important;
  letter-spacing: 0.16em !important;
  text-transform: uppercase !important;
  color: var(--mint-deep) !important;
  margin-bottom: 4px !important;
}
.lucent-insight-host--print .callout-body {
  font-size: 10.5pt;
  line-height: 1.55;
  color: var(--card-ink) !important;
}
.lucent-insight-host--print .callout--warn { border-left-color: #d4a017 !important; }
.lucent-insight-host--print .callout--warn .callout-title { color: #d4a017 !important; }
.lucent-insight-host--print .callout--danger { border-left-color: #c1442e !important; }
.lucent-insight-host--print .callout--danger .callout-title { color: #c1442e !important; }
.lucent-insight-host--print .callout--success { border-left-color: var(--mint-deep) !important; }
.lucent-insight-host--print .callout--success .callout-title { color: var(--mint-deep) !important; }

/* ---------- Dividers (sbi.ui.divider) ---------- */
.lucent-insight-host--print hr,
.lucent-insight-host--print .divider {
  border: 0 !important;
  border-top: 1px solid var(--rule) !important;
  margin: 18px 0 !important;
}

/* ---------- PrintStaticControl ----------
 * Designed filter chip: pill-shaped, soft mint tint, sans-serif.
 * Reads as "this report was generated with these inputs applied"
 * — a visible-but-quiet provenance marker, not a mono key/value
 * data row. */
.lucent-insight-host--print .print-static {
  display: inline-flex !important;
  align-items: baseline !important;
  gap: 6px !important;
  background: var(--mint-wash) !important;
  border: 1px solid var(--mint-wash-edge) !important;
  border-radius: 999px !important;
  padding: 5px 14px !important;
  margin: 0 6px 6px 0 !important;
  font-family: "Geist", system-ui, sans-serif !important;
  font-size: 10pt !important;
}
.lucent-insight-host--print .print-static-label {
  color: var(--mint-deep) !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 9.5pt !important;
  margin-right: 2px;
}
.lucent-insight-host--print .print-static-value {
  color: var(--card-ink) !important;
  font-weight: 600 !important;
}

/* Top-of-body sibling spacing — gentler than the section margin
 * since these are flat layout nodes from the SBI source. */
.lucent-insight-host--print > * + * { margin-top: 14px; }
</style>